import Button from "@bmi-digital/components/button";
import ContainerDialog from "@bmi-digital/components/container-dialog";
import Grid from "@bmi-digital/components/grid";
import SelectRoof from "@bmi-digital/components/icon/SelectRoof";
import SelectTile from "@bmi-digital/components/icon/SelectTile";
import SelectWallColour from "@bmi-digital/components/icon/SelectWallColour";
import TileColourIcon from "@bmi-digital/components/icon/TileColour";
import ToggleCard from "@bmi-digital/components/toggle-card";
import Typography from "@bmi-digital/components/typography";
import SvgIcon from "@mui/material/SvgIcon";
import useMediaQuery from "@mui/material/useMediaQuery";
import classnames from "classnames";
import React, {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { microCopy } from "@bmi/microcopies";
import type { MicroCopyValues } from "@bmi/microcopies";
import { useConfig } from "../../contexts/ConfigProvider";
import { devLog } from "../../utils/devLog";
import { queryElasticSearch } from "../../utils/elasticSearch";
import ProgressIndicator from "../ProgressIndicator";
import { useSiteContext } from "../Site";
import getRef from "./GetRef";
import HouseViewer from "./HouseViewer";
import TileViewer from "./TileViewer";
import { Category, HouseType, PIMTile, Siding } from "./Types";
import { getProductsQuery } from "./helpers/esQuery";
import { prepareProducts } from "./helpers/products";
import {
  StyledActions,
  StyledChildDialog,
  StyledParentDialog,
  classes,
  Title
} from "./styles/VisualiserStyles";
import VisualiserSubHeader, { type VisualiserHeaderProps } from "./SubHeader";

export type Parameters = {
  tileId?: string | number;
  colourId?: number;
  sidingId?: number;
  viewMode?: "tile" | "roof";
  sidings: Siding[];
};

const categoryToMicroCopyMap: Record<Category, MicroCopyValues> = {
  clay: microCopy.VISUALISER_MATERIALS_CLAY,
  concrete: microCopy.VISUALISER_MATERIALS_CONCRETE,
  metal: microCopy.VISUALISER_MATERIALS_METAL
};

export type Props = {
  contentSource: string;
  open: boolean;
  onClose: () => void;
  onChange?: (params: Partial<Parameters & { isOpen: boolean }>) => void;
  shareWidget?: React.JSX.Element;
  houseTypes: HouseType[] | null;
  onClick: (
    params: Partial<Parameters> & {
      type: string;
      label: string;
      data?: Record<string, any>;
    }
  ) => void;
} & Parameters;

type TileSectorDialogProps = {
  activeTile: PIMTile;
  contentSource: string;
  open: boolean;
  onCloseClick: (isTileSelectorOpen: boolean) => void;
  onButtonClick: (data: { tileId: string; label: string }) => void;
  tiles: PIMTile[];
};

const Actions = ({
  toggleTileSelector,
  toggleSidingsSelector,
  setViewMode,
  viewMode,
  onButtonClick
}: {
  toggleTileSelector: Dispatch<(previousTileSelectorOpen: boolean) => boolean>;
  toggleSidingsSelector: Dispatch<
    (previousSidingsSelectorOpen: boolean) => boolean
  >;
  setViewMode: (data: { viewMode: Props["viewMode"] }) => void;
  viewMode: Props["viewMode"];
  onButtonClick: (data: { type: string; label: string }) => void;
}) => {
  const isXsMobile = useMediaQuery("(max-width: 376px)");
  const { getMicroCopy } = useSiteContext();
  return (
    <StyledActions>
      <Button
        hasDarkBackground
        variant="text"
        startIcon={<SvgIcon component={TileColourIcon} />}
        onClick={() => {
          toggleTileSelector(
            (previousTileSelectorOpen: boolean) => !previousTileSelectorOpen
          );
          onButtonClick({
            type: "bottom-menu",
            label: getMicroCopy(microCopy.VISUALISER_ACTIONS_SELECT_PRODUCT)
          });
        }}
      >
        {!isXsMobile &&
          getMicroCopy(microCopy.VISUALISER_ACTIONS_SELECT_PRODUCT)}
      </Button>
      <Button
        hasDarkBackground
        variant="text"
        startIcon={<SvgIcon component={SelectWallColour} />}
        disabled={viewMode !== "roof"}
        onClick={() => {
          toggleSidingsSelector(
            (previousSidingsSelectorOpen: boolean) =>
              !previousSidingsSelectorOpen
          );
          onButtonClick({
            type: "bottom-menu",
            label: getMicroCopy(microCopy.VISUALISER_ACTIONS_WALL_COLOUR)
          });
        }}
      >
        {!isXsMobile && getMicroCopy(microCopy.VISUALISER_ACTIONS_WALL_COLOUR)}
      </Button>
      {viewMode === "tile" && (
        <Button
          hasDarkBackground
          variant="text"
          startIcon={<SvgIcon component={SelectRoof} />}
          onClick={() => {
            setViewMode({ viewMode: "roof" });
            onButtonClick({
              type: "bottom-menu",
              label: getMicroCopy(microCopy.VISUALISER_ACTIONS_ROOF_MODE)
            });
          }}
        >
          {!isXsMobile && getMicroCopy(microCopy.VISUALISER_ACTIONS_ROOF_MODE)}
        </Button>
      )}
      {viewMode === "roof" && (
        <Button
          hasDarkBackground
          variant="text"
          startIcon={<SvgIcon component={SelectTile} />}
          onClick={() => {
            setViewMode({ viewMode: "tile" });
            onButtonClick({
              type: "bottom-menu",
              label: getMicroCopy(microCopy.VISUALISER_ACTIONS_TILE_MODE)
            });
          }}
        >
          {!isXsMobile && getMicroCopy(microCopy.VISUALISER_ACTIONS_TILE_MODE)}
        </Button>
      )}
    </StyledActions>
  );
};

const SelectionOptions = ({
  defaultValue,
  products,
  title,
  onClick
}: {
  contentSource: string;
  defaultValue: string;
  products: PIMTile[];
  title: string;
  onClick: (data: { tileId: string; label: string }) => void;
}) => {
  const { getMicroCopy } = useSiteContext();

  return (
    <div>
      <Typography variant="h5" component="h3" className={classes.groupTitle}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {products.map((tile) => (
          <Grid key={tile.code} xs={6} md={4} lg={2}>
            <ToggleCard
              component="button"
              title={tile.name}
              imageSource={tile.mainImage}
              onClick={() => {
                onClick({
                  tileId: tile.code,
                  label: `${tile.name} + ${tile.colour}`
                });
              }}
              className={classnames(
                defaultValue === tile.code && classes.activeSelectionOption
              )}
              aria-label={
                defaultValue === tile.code
                  ? getMicroCopy(microCopy.VISUALISER_SELECTION_OPTION_DEFAULT)
                  : undefined
              }
            >
              <ToggleCard.Paragraph>{tile.colour}</ToggleCard.Paragraph>
            </ToggleCard>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

const TileSectorDialog = ({
  activeTile,
  contentSource,
  open,
  onCloseClick,
  onButtonClick,
  tiles
}: TileSectorDialogProps) => {
  const { getMicroCopy } = useSiteContext();

  const categories = Object.values(Category);

  return (
    <StyledChildDialog
      open={open}
      onCloseClick={() => onCloseClick(false)}
      maxWidth="xl"
      color="pearl"
      className={classnames(classes.root, classes.secondary)}
      containerClassName={classes.container}
      backdropProps={{ invisible: true }}
      allowOverflow
    >
      <Typography
        variant="h4"
        component="h2"
        align="center"
        className={classes.groupTitle}
      >
        {getMicroCopy(microCopy.VISUALISER_TILE_SELECTOR_TITLE)}
      </Typography>
      <Typography gutterBottom>
        {getMicroCopy(microCopy.VISUALISER_TILE_SELECTOR_DESCRIPTION)}
      </Typography>

      {categories.map((category) => {
        const products = tiles.filter((tile) => tile.category === category);

        if (!products.length) {
          return;
        }

        return (
          <SelectionOptions
            contentSource={contentSource}
            defaultValue={activeTile.code}
            key={`material-group-${category}`}
            //eslint-disable-next-line security/detect-object-injection
            title={getMicroCopy(categoryToMicroCopyMap[category])}
            products={products}
            onClick={onButtonClick}
          />
        );
      })}
    </StyledChildDialog>
  );
};

const SidingsSelectorDialog = ({
  open,
  onCloseClick,
  activeSiding,
  sidings,
  contentSource,
  onConfirmClick,
  onClick
}: {
  open: boolean;
  onCloseClick: (isSidingsSelectorOpen: boolean) => void;
  activeSiding: Siding;
  sidings: Siding[];
  contentSource: string;
  onConfirmClick: (data: { sidingId: number }) => void;
  onClick: (event: { type: string; label: string }) => void;
}) => {
  const { getMicroCopy } = useSiteContext();

  return (
    <StyledChildDialog
      open={open}
      onCloseClick={() => onCloseClick(false)}
      maxWidth="xl"
      color="pearl"
      className={classnames(classes.root, classes.secondary)}
      containerClassName={classes.container}
      backdropProps={{ invisible: true }}
      allowOverflow
    >
      <Typography
        variant="h4"
        component="h2"
        align="center"
        className={classes.groupTitle}
      >
        {getMicroCopy(microCopy.VISUALISER_SIDING_SELECTOR_TITLE)}
      </Typography>
      <Grid container spacing={2}>
        {sidings.map(({ id, name, diffuseMapRef }) => (
          <Grid key={id} xs={6} md={4} lg={2}>
            <ToggleCard
              component="button"
              title={getMicroCopy(name)}
              imageSource={getRef(diffuseMapRef, {
                size: "original",
                contentSource
              })}
              onClick={() => {
                onConfirmClick({ sidingId: id });
                onCloseClick(false);
                onClick({
                  type: "wall-selector",
                  label: getMicroCopy(name)
                });
              }}
              className={classnames(
                activeSiding.id === id && classes.activeSelectionOption
              )}
              aria-label={
                activeSiding.id === id
                  ? getMicroCopy(microCopy.VISUALISER_SELECTION_OPTION_DEFAULT)
                  : undefined
              }
            />
          </Grid>
        ))}
      </Grid>
    </StyledChildDialog>
  );
};

const viewerComponentMap = {
  tile: TileViewer,
  roof: HouseViewer
};

const Visualiser = ({
  contentSource,
  open,
  tileId,
  sidingId,
  viewMode,
  sidings,
  shareWidget,
  onClose,
  onChange,
  onClick,
  houseTypes
}: Props) => {
  const [isTileSelectorOpen, setIsTileSelectorOpen] = useState<boolean>(false);
  const [isSidingsSelectorOpen, setIsSidingsSelectorOpen] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { esIndexNameProduct } = useConfig();
  const [state, _setState] = useState({ tileId, sidingId, viewMode });
  const [tiles, setTiles] = useState<PIMTile[]>([]);
  const { getMicroCopy } = useSiteContext();

  const stateRef = React.useRef(state);
  const setState = useCallback(
    (data) => {
      stateRef.current = { ...state, ...data };
      _setState((previousState) => {
        return { ...previousState, ...data };
      });
    },
    [state]
  );

  const fetchVisualiserData = async () => {
    setIsLoading(true);
    try {
      const res = await queryElasticSearch(
        getProductsQuery(),
        esIndexNameProduct
      );
      const hits = res.hits.hits.map((hit) => hit._source);
      setTiles(prepareProducts(hits));
    } catch (err) {
      devLog("Failed to fetch data", err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchVisualiserData();
  }, []);

  useEffect(() => {
    setState({ tileId, sidingId, viewMode });
  }, [tileId, sidingId, viewMode]);

  useEffect(() => {
    if (onChange) {
      const { tileId, sidingId, viewMode } = state;

      onChange({
        sidingId,
        tileId,
        viewMode,
        isOpen: open
      });
    }
  }, [state, open]);

  const handleOnClick = useCallback(
    ({
      type,
      label,
      data
    }: {
      type: string;
      label: string;
      data?: { variantCode: string; productPath: string };
    }) => {
      const { tileId, sidingId, viewMode } = stateRef.current;

      onClick({
        sidingId,
        tileId,
        viewMode,
        type,
        label,
        data
      });
    },
    [onClick, stateRef]
  );

  const handleReadMoreButtonClick: VisualiserHeaderProps["onReadMoreBtnClick"] =
    useCallback(
      (params) => {
        handleOnClick(params);
        onClose();
      },
      [handleOnClick, onClose]
    );

  const activeTile = useMemo(() => {
    if (!tiles.length) {
      return;
    }
    return (
      tiles.find(({ code }) => code === state.tileId) ||
      tiles.find(({ code }) => code === tileId) ||
      tiles[0]
    );
  }, [tiles, state.tileId, tileId]);

  const activeSiding = useMemo(
    () =>
      sidings.find(({ id }) => id === state.sidingId) ||
      sidings.find(({ id }) => id === sidingId) ||
      sidings[0],
    [sidings, sidingId, state.sidingId]
  );

  const viewerProps = {
    tile: activeTile,
    options: { contentSource },
    siding: activeSiding,
    setIsLoading: (isLoading: boolean) => setIsLoading(isLoading),
    ...(state.viewMode === "roof"
      ? { houseModelUrl: houseTypes?.[0]?.url }
      : {})
  };

  const Viewer = viewerComponentMap[state.viewMode || "tile"];

  return (
    <StyledParentDialog
      open={open}
      onCloseClick={onClose}
      onBackdropClick={onClose}
      maxWidth="xl"
      containerClassName={classes.content}
    >
      <ContainerDialog.Header className={classes.header} onCloseClick={onClose}>
        <>
          {activeTile && (
            <VisualiserSubHeader
              activeTile={activeTile}
              onReadMoreBtnClick={handleReadMoreButtonClick}
              shareWidget={shareWidget}
            />
          )}
          <Title variant="h3" data-testid="visualiser-title">
            {getMicroCopy(microCopy.VISUALISER_TITLE)}
          </Title>
        </>
      </ContainerDialog.Header>
      <div className={classnames(classes.container, classes.viewer)}>
        {isLoading && (
          <div className={classes.progressContainer}>
            <ProgressIndicator size={40} />
          </div>
        )}
        {viewMode && activeTile && <Viewer {...viewerProps} />}
        <TileSectorDialog
          open={isTileSelectorOpen}
          onCloseClick={setIsTileSelectorOpen}
          activeTile={activeTile}
          tiles={tiles}
          contentSource={contentSource}
          onButtonClick={({ tileId, label }) => {
            setIsTileSelectorOpen(false);
            setState({ tileId });
            handleOnClick({ type: "product-selector", label });
          }}
        />
        <SidingsSelectorDialog
          open={isSidingsSelectorOpen}
          onCloseClick={setIsSidingsSelectorOpen}
          activeSiding={activeSiding}
          sidings={sidings}
          onConfirmClick={setState}
          contentSource={contentSource}
          onClick={handleOnClick}
        />
      </div>

      <Actions
        toggleTileSelector={setIsTileSelectorOpen}
        toggleSidingsSelector={setIsSidingsSelectorOpen}
        viewMode={state.viewMode}
        setViewMode={setState}
        onButtonClick={handleOnClick}
      />
    </StyledParentDialog>
  );
};

export default Visualiser;
